import { useState, useRef, useEffect } from "react";
import { musteriYorumlari } from "@/mocks/yorumlar";

export default function MusteriYorumlari() {
  const [aktifIndex, setAktifIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const autoRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const toplam = musteriYorumlari.length;

  const goTo = (index: number) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setAktifIndex((index + toplam) % toplam);
      setAnimating(false);
    }, 250);
  };

  const ileri = () => goTo(aktifIndex + 1);
  const geri = () => goTo(aktifIndex - 1);

  useEffect(() => {
    autoRef.current = setInterval(() => {
      setAktifIndex((prev) => (prev + 1) % toplam);
    }, 5000);
    return () => {
      if (autoRef.current) clearInterval(autoRef.current);
    };
  }, [toplam]);

  const resetAuto = () => {
    if (autoRef.current) clearInterval(autoRef.current);
    autoRef.current = setInterval(() => {
      setAktifIndex((prev) => (prev + 1) % toplam);
    }, 5000);
  };

  const handleNav = (fn: () => void) => {
    fn();
    resetAuto();
  };

  const getVisible = () => {
    const prev = (aktifIndex - 1 + toplam) % toplam;
    const next = (aktifIndex + 1) % toplam;
    return [prev, aktifIndex, next];
  };

  const visibleIndexes = getVisible();

  return (
    <section id="yorumlar" className="py-20 md:py-28 bg-[#F7F3EE] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block bg-[#1A0F0A] text-[#E8B86D] text-xs font-bold px-5 py-2 rounded-full tracking-[0.2em] uppercase mb-5">
            Müşteri Deneyimleri
          </span>
          <h2
            className="font-black italic text-[#1A0F0A] leading-tight mb-4"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(2rem, 5vw, 3.8rem)",
            }}
          >
            Onlar Anlatsın,
            <br />
            <span
              style={{
                background: "linear-gradient(135deg, #C9943A 0%, #E8B86D 50%, #C9943A 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Siz Karar Verin
            </span>
          </h2>
          <p className="text-[#6B5D52] text-base md:text-lg max-w-xl mx-auto font-light">
            5.000&apos;den fazla mutlu müşterimizin gerçek deneyimleri
          </p>

          {/* Overall rating */}
          <div className="flex items-center justify-center gap-3 mt-6">
            <div className="flex gap-1">
              {[1,2,3,4,5].map((s) => (
                <i key={s} className="ri-star-fill text-[#E8B86D] text-xl" />
              ))}
            </div>
            <span
              className="text-[#1A0F0A] font-black text-xl italic"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              4.9
            </span>
            <span className="text-[#8B7D6B] text-sm font-light">/ 5 · 847 değerlendirme</span>
          </div>
        </div>

        {/* Desktop: 3-card slider */}
        <div className="hidden md:block relative">
          <div className="grid grid-cols-3 gap-6">
            {visibleIndexes.map((yorumIdx, pos) => {
              const yorum = musteriYorumlari[yorumIdx];
              const isCenter = pos === 1;
              return (
                <div
                  key={yorum.id}
                  className={`rounded-2xl p-7 transition-all duration-500 border ${
                    isCenter
                      ? "bg-[#1A0F0A] border-[#E8B86D]/30 scale-105"
                      : "bg-white border-[#EDE5D8] opacity-70"
                  }`}
                >
                  {/* Stars */}
                  <div className="flex gap-1 mb-4">
                    {[1,2,3,4,5].map((s) => (
                      <i key={s} className="ri-star-fill text-[#E8B86D] text-sm" />
                    ))}
                  </div>

                  {/* Quote icon */}
                  <i className={`ri-double-quotes-l text-3xl mb-3 block ${isCenter ? "text-[#E8B86D]" : "text-[#D4C4B0]"}`} />

                  {/* Text */}
                  <p className={`text-sm leading-relaxed mb-6 font-light ${isCenter ? "text-white/85" : "text-[#4A4A4A]"}`}>
                    {yorum.yorum}
                  </p>

                  {/* Hizmet etiketi */}
                  <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full mb-5 ${
                    isCenter ? "bg-[#E8B86D]/15 text-[#E8B86D] border border-[#E8B86D]/25" : "bg-[#1A0F0A] text-[#E8B86D]"
                  }`}>
                    {yorum.hizmet}
                  </span>

                  {/* Author */}
                  <div className={`flex items-center gap-3 pt-4 border-t ${isCenter ? "border-white/10" : "border-[#EDE5D8]"}`}>
                    <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-[#E8B86D]/30">
                      <img src={yorum.avatar} alt={yorum.isim} className="w-full h-full object-cover object-top" />
                    </div>
                    <div>
                      <p className={`font-bold text-sm ${isCenter ? "text-white" : "text-[#1A0F0A]"}`}>{yorum.isim}</p>
                      <p className={`text-xs font-light ${isCenter ? "text-white/45" : "text-[#B0A090]"}`}>
                        {yorum.sehir} · {yorum.tarih}
                      </p>
                    </div>
                    <div className="ml-auto">
                      <i className={`ri-verified-badge-fill text-lg ${isCenter ? "text-[#E8B86D]" : "text-[#E8B86D]/40"}`} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Nav arrows */}
          <button
            onClick={() => handleNav(geri)}
            className="absolute -left-5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-[#1A0F0A] border border-[#E8B86D]/20 text-white hover:bg-[#E8B86D] hover:text-[#1A0F0A] hover:border-[#E8B86D] transition-all duration-200 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            onClick={() => handleNav(ileri)}
            className="absolute -right-5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-[#1A0F0A] border border-[#E8B86D]/20 text-white hover:bg-[#E8B86D] hover:text-[#1A0F0A] hover:border-[#E8B86D] transition-all duration-200 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>

        {/* Mobile: single card */}
        <div className="md:hidden">
          <div className={`bg-[#1A0F0A] rounded-2xl p-6 transition-opacity duration-300 ${animating ? "opacity-0" : "opacity-100"}`}>
            {(() => {
              const yorum = musteriYorumlari[aktifIndex];
              return (
                <>
                  <div className="flex gap-1 mb-3">
                    {[1,2,3,4,5].map((s) => (
                      <i key={s} className="ri-star-fill text-[#E8B86D] text-sm" />
                    ))}
                  </div>
                  <i className="ri-double-quotes-l text-2xl text-[#E8B86D] mb-3 block" />
                  <p className="text-white/85 text-sm leading-relaxed mb-4 font-light">{yorum.yorum}</p>
                  <span className="inline-block bg-[#E8B86D]/15 text-[#E8B86D] text-xs font-bold px-3 py-1 rounded-full mb-4 border border-[#E8B86D]/25">
                    {yorum.hizmet}
                  </span>
                  <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-[#E8B86D]/30">
                      <img src={yorum.avatar} alt={yorum.isim} className="w-full h-full object-cover object-top" />
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm">{yorum.isim}</p>
                      <p className="text-white/45 text-xs font-light">{yorum.sehir} · {yorum.tarih}</p>
                    </div>
                    <i className="ri-verified-badge-fill text-[#E8B86D] text-lg ml-auto" />
                  </div>
                </>
              );
            })()}
          </div>

          <div className="flex items-center justify-center gap-4 mt-6">
            <button onClick={() => handleNav(geri)} className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A0F0A] text-white hover:bg-[#E8B86D] hover:text-[#1A0F0A] transition-all cursor-pointer">
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <div className="flex gap-2">
              {musteriYorumlari.map((_, i) => (
                <button
                  key={i}
                  onClick={() => handleNav(() => goTo(i))}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${i === aktifIndex ? "w-6 h-2 bg-[#E8B86D]" : "w-2 h-2 bg-[#D4C4B0]"}`}
                />
              ))}
            </div>
            <button onClick={() => handleNav(ileri)} className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A0F0A] text-white hover:bg-[#E8B86D] hover:text-[#1A0F0A] transition-all cursor-pointer">
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        </div>

        {/* Dots - desktop */}
        <div className="hidden md:flex justify-center gap-2 mt-8">
          {musteriYorumlari.map((_, i) => (
            <button
              key={i}
              onClick={() => handleNav(() => goTo(i))}
              className={`rounded-full transition-all duration-300 cursor-pointer ${i === aktifIndex ? "w-6 h-2 bg-[#E8B86D]" : "w-2 h-2 bg-[#D4C4B0] hover:bg-[#E8B86D]/50"}`}
            />
          ))}
        </div>

        {/* Trust bar */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { ikon: "ri-google-fill", platform: "Google", puan: "4.9", renk: "text-[#EA4335]" },
            { ikon: "ri-instagram-line", platform: "Instagram", puan: "5.0", renk: "text-[#E8B86D]" },
            { ikon: "ri-facebook-circle-fill", platform: "Facebook", puan: "4.8", renk: "text-[#1877F2]" },
            { ikon: "ri-star-smile-line", platform: "Toplam Yorum", puan: "847+", renk: "text-[#E8B86D]" },
          ].map((item) => (
            <div key={item.platform} className="bg-[#1A0F0A] rounded-xl p-4 flex items-center gap-3 border border-[#E8B86D]/15">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/8 flex-shrink-0">
                <i className={`${item.ikon} text-xl ${item.renk}`} />
              </div>
              <div>
                <p
                  className="text-white font-black text-base italic"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {item.puan}
                </p>
                <p className="text-white/40 text-xs font-light">{item.platform}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}